import { Fragment, useCallback, useEffect, useState } from 'react'
import Refresh from '@material-ui/icons/Refresh'
import ArrowBack from '@material-ui/icons/ArrowBack'
import clsx from 'clsx'
import CheckboxSelectorV2 from 'app/componentV4/Filters/CheckboxSelectorV2'
import {
  CHANNEL_APP,
  CHANNEL_CALL,
  CHANNEL_EMAIL,
  CHANNEL_SMS,
  filterChannels,
} from 'app/constants/channel'
import { useIntl } from 'react-intl'
import { useAppName } from 'commons/usePartner'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import effects from 'app/effects/effects'
import z from 'zod'
import { getLocalizedDate, timeAgo } from 'commons/date'
import { useInView } from 'react-intersection-observer'
import ChannelIcon from 'app/componentV4/Channel/ChannelIcon'
import { CircularProgress } from '@material-ui/core'
import Avatars from 'app/componentV4/Avatar/Avatars'

export default function NewMetricsRecipientList({
  id,
  metricsType,
  onDismiss,
}) {
  const { formatMessage } = useIntl()
  const [fetchId, setFetchId] = useState(new Date())
  const [lastRefresh, setLastRefresh] = useState()
  const [lastRefreshText, setLastRefreshText] = useState()

  useEffect(() => {
    if (!lastRefresh) return
    const token = setInterval(() => {
      const newAgo = timeAgo(lastRefresh)
      if (newAgo !== lastRefreshText) setLastRefreshText(newAgo)
    }, 1000)
    return () => {
      clearInterval(token)
    }
  }, [lastRefresh, lastRefreshText])

  const [filterByChannels, setFilterByChannels] = useState([])

  const queryClient = useQueryClient()
  const filterByChannelsKey =
    filterByChannels && Boolean(filterByChannels.length)
      ? filterByChannels
          .map((channel) => channelTypeFromChannelId(channel.name))
          .join(',')
      : undefined
  const queryKey = [
    'message',
    id,
    'metrics',
    'recipients',
    metricsType,
    filterByChannelsKey,
    fetchId,
  ]
  const recipientsByMetricsQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      effects.apis.message.metric
        .recipientsByMetrics({
          messageId: id,
          metricsType,
          pageSize: 20,
          messageChannels: filterByChannelsKey,
          pageAfter: pageParam,
        })
        .then((data) => {
          if (!pageParam) {
            const lastRefresh = new Date()
            setLastRefresh(lastRefresh)
            setLastRefreshText(timeAgo(lastRefresh))
          }
          return recipientsByMetricsSchema.parse(data)
        }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _pages) => {
      return lastPage && lastPage.page ? lastPage.page.next : undefined
    },
  })

  const { ref: inFetchNextPageRef, inView: inFetchNextPageView } = useInView()

  useEffect(() => {
    if (inFetchNextPageView) {
      recipientsByMetricsQuery.fetchNextPage()
    }
  }, [inFetchNextPageView, recipientsByMetricsQuery.fetchNextPage])

  useEffect(() => {
    setFetchId(new Date())
  }, [])

  const appName = useAppName()

  const metricMessages = {
    delivered: formatMessage({ id: 'metrics.delivered' }),
    'not-delivered': formatMessage({ id: 'metrics.not.delivered' }),
    'in-progress': formatMessage({ id: 'metrics.delivering' }),
    viewed: formatMessage({ id: 'metrics.message.views' }),
    liked: formatMessage({ id: 'metrics.reaction' }),
    listened: formatMessage({ id: 'metrics.listen.text.to.speech' }),
    reported: formatMessage({ id: 'metrics.report' }),
  }

  const metricsTitle = metricMessages[metricsType] || 'Unknown'

  return (
    <div className="space-y-20">
      <div className="flex items-center justify-between">
        <div className="space-x-8 flex items-center">
          <button
            className="size-20"
            onClick={() => {
              onDismiss()
            }}
          >
            <ArrowBack className="size-20" />
          </button>
          <span className="text-14 text-primary-400 font-medium">
            {metricsTitle}
          </span>
        </div>

        <div className="flex text-primary-400 justify-between items-center space-x-4">
          <span className="text-14 text-primary-300">
            {formatMessage({
              id: 'text.last.updated.plain',
            })}{' '}
            {lastRefreshText}
          </span>
          <button
            type="button"
            className="space-x-2 flex items-center"
            disabled={recipientsByMetricsQuery.isFetching}
            onClick={() => {
              recipientsByMetricsQuery.refetch()
            }}
          >
            <Refresh className="size-14 shrink-0" />
            <span
              className={clsx(
                'text-12 underline',
                recipientsByMetricsQuery.isFetching && 'text-primary-300',
              )}
            >
              {recipientsByMetricsQuery.isFetching
                ? formatMessage({
                    id: 'metrics.refreshing',
                  })
                : formatMessage({
                    id: 'metrics.refresh.data',
                  })}
            </span>
          </button>
        </div>
      </div>

      <CheckboxSelectorV2
        name="filterChannels"
        label={formatMessage({ id: 'channels.label' })}
        options={filterChannels(appName)}
        value={filterByChannels}
        onChange={(value) => {
          setFilterByChannels(value)
        }}
        className="mr-5 bg-primary-200 text-primary text-14 font-normal"
        isSelectAll={true}
      />

      {recipientsByMetricsQuery.status === 'pending' ? (
        <div className="flex w-full justify-center items-center p-12 h-[calc(100svh-188px)]">
          <CircularProgress />
        </div>
      ) : recipientsByMetricsQuery.status === 'error' ? (
        <p>Error: {recipientsByMetricsQuery.error.message}</p>
      ) : recipientsByMetricsQuery.data ? (
        <div className="overflow-y-auto h-[calc(100svh-188px)]">
          <div>
            <table className="w-full text-left">
              <thead className="text-14 text-primary-400">
                <tr>
                  <th scope="col" className="p-12 border-b border-primary-350">
                    {formatMessage({
                      id: 'metrics.recipient.details',
                    })}
                  </th>
                  <th scope="col" className="p-12 border-b border-primary-350">
                    {formatMessage({
                      id: 'metrics.last.seen',
                    })}
                  </th>
                  <th
                    scope="col"
                    className="p-12 border-b border-primary-350 text-nowrap"
                  >
                    {formatMessage({
                      id: 'metrics.channel',
                    })}
                  </th>
                  {['not-delivered', 'reported'].includes(metricsType) && (
                    <th
                      scope="col"
                      className="p-12 border-b border-primary-350"
                    >
                      {formatMessage({
                        id: 'metrics.reason',
                      })}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {recipientsByMetricsQuery.data.pages.map((page, i) => {
                  return (
                    <Fragment key={page.page.next}>
                      {page.data.map((recipient) => {
                        return (
                          <tr className="" key={recipient.historyId}>
                            <th
                              scope="row"
                              className="p-12 text-14 text-gray-400 whitespace-nowrap border-b border-primary-350"
                            >
                              <div className="flex gap-3">
                                <Avatars
                                  id={recipient.historyId}
                                  name={`${recipient.firstName} ${recipient.lastName}`}
                                  src={recipient.profilePicture}
                                  characters={recipient.profileInitials}
                                  className="size-20 text-12"
                                />
                                {recipient.firstName} {recipient.lastName}
                              </div>
                            </th>
                            <th
                              scope="row"
                              className="p-12 text-14 text-gray-400 whitespace-nowrap border-b border-primary-350"
                            >
                              {recipient.lastSeenDate
                                ? getLocalizedDate(
                                    recipient.lastSeenDate,
                                    'DATE_TIME',
                                  )
                                : '-'}
                            </th>
                            <th
                              scope="row"
                              className="p-12 text-14 text-gray-400 whitespace-nowrap border-b border-primary-350"
                            >
                              <ChannelIcon
                                channel={channelIdFromChannelType(
                                  recipient.channel,
                                )}
                              />
                            </th>
                            {metricsType === 'not-delivered' && (
                              <th
                                scope="row"
                                className="p-12 text-14 text-gray-400 whitespace-nowrap border-b border-primary-350"
                              >
                                {recipient.notDeliveredReason || '-'}
                              </th>
                            )}
                            {metricsType === 'reported' && (
                              <th
                                scope="row"
                                className="p-12 text-14 text-gray-400 whitespace-nowrap border-b border-primary-350"
                              >
                                {recipient.reportedType
                                  ? formatMessage({
                                      id: reportedReasonByReportedType(
                                        recipient.reportedType,
                                      ),
                                    })
                                  : '-'}
                              </th>
                            )}
                          </tr>
                        )
                      })}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>

            <div ref={inFetchNextPageRef} className="w-full p-12">
              {!recipientsByMetricsQuery.hasNextPage ? null : !recipientsByMetricsQuery.isFetchingNextPage &&
                recipientsByMetricsQuery.error ? (
                <div className="flex w-full justify-center">
                  <pre>{recipientsByMetricsQuery.error}</pre>
                </div>
              ) : (
                <div className="flex w-full justify-center">
                  <CircularProgress />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function formatDate(dateString) {
  const date = new Date(dateString)

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date)
}

function channelTypeFromChannelId(channelId) {
  switch (channelId) {
    case CHANNEL_APP:
      return 'app'
    case CHANNEL_SMS:
      return 'sms'
    case CHANNEL_EMAIL:
      return 'email'
    case CHANNEL_CALL:
      return 'automated-call'
  }
}

function channelIdFromChannelType(channelType) {
  switch (channelType) {
    case 'app':
      return CHANNEL_APP
    case 'sms':
      return CHANNEL_SMS
    case 'email':
      return CHANNEL_EMAIL
    case 'automated-call':
      return CHANNEL_CALL
    default:
      throw new Error(`Invalid channel type: ${channelType}`)
  }
}

function reportedReasonByReportedType(reportedType) {
  switch (reportedType) {
    case 1:
      return 'dashboard.lastNotification.analytics.reported.sexualContent'
    case 2:
      return 'dashboard.lastNotification.analytics.reported.violentContent'
    case 3:
      return 'dashboard.lastNotification.analytics.reported.hatefulContent'
    case 4:
      return 'dashboard.lastNotification.analytics.reported.harmfulContent'
    case 5:
      return 'dashboard.lastNotification.analytics.reported.spamContent'
    default:
      return 'Unknown'
  }
}

export const channelTypeSchema = z.enum([
  'app',
  'sms',
  'email',
  'automated-call',
])

const recipientsByMetricsSchema = z.object({
  data: z.array(
    z.object({
      historyId: z.number(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      channel: channelTypeSchema,
      notDeliveredReason: z.string().nullable(),
      reportedType: z.number().nullable(),
      lastSeenDate: z
        .string()
        .datetime()
        .nullable()
        .transform((val) => (val ? new Date(val) : null)),
      lastActiveDate: z
        .string()
        .datetime()
        .nullable()
        .transform((val) => (val ? new Date(val) : null)),
      profilePicture: z.string().nullable(),
      profileInitials: z.string().nullable(),
    }),
  ),
  page: z.object({
    next: z.string().nullable(),
  }),
})
